import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { useNavigate } from 'react-router-dom';
import { RECORD_TYPE, recordSchema } from '@/entities/cmo-record/model/types';
import { getPresignedUrl, uploadFileToS3 } from '@/features/records/api/file-upload';
import { getErrorMessage } from '@/shared/api/api';
import { SwitchCase } from '@/shared/headless';
import { Button, Input, Label } from '@/shared/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { ScrollArea } from '@/shared/ui/scroll-area';
import { Text } from '@/shared/ui/text';
import { formatFileSize } from '@/shared/utils';
import { useCMORecords } from '../model/context';

interface Step1Props {
  readonly step: number;
}

export function Step1({ step }: Step1Props) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadedFile, setUploadedFile, setRecordType, recordType } = useCMORecords();

  const { mutate, isPending } = useMutation({
    mutationFn: async (file: File) => {
      // 1단계: Presigned URL 요청
      const presignedData = await getPresignedUrl(file, 'RECORD');

      // 2단계: 새 파일인 경우 S3에 업로드
      if (presignedData.state === 'new' && presignedData.presigned_url) {
        await uploadFileToS3(file, presignedData.presigned_url, presignedData.content_type);
      }

      return { dk_document_id: presignedData.dk_document_id };
    },
    onSuccess: (data) => {
      navigate(`/cmo-records/${step + 1}?dk_document_id=${data.dk_document_id}`);
    },
    onError: async (error) => {
      const errorMessage = await getErrorMessage(error);
      overlay.open(({ isOpen, close }) => (
        <Dialog
          onOpenChange={close}
          open={isOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>파일 검증 오류</DialogTitle>
              <DialogDescription>
                {errorMessage || '파일 검증 중 오류가 발생했습니다. 파일을 다시 확인해주세요.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => close()}
                variant={'primary'}
              >
                닫기
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));
    },
  });

  const handleFileUploadLabelButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      event.target.value = '';
    }
  };

  const handleFileRemove = () => {
    setUploadedFile(null);
    setRecordType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
    const submittedRecordType = submitter?.value as keyof typeof RECORD_TYPE;

    const validatedFields = recordSchema.safeParse({
      recordType: submittedRecordType,
      file: uploadedFile,
    });

    if (!validatedFields.success) {
      overlay.open(({ isOpen, close }) => (
        <Dialog
          onOpenChange={close}
          open={isOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>파일 업로드 오류</DialogTitle>
              <DialogDescription>
                {validatedFields.error.errors.map((error) => error.message).join(', ')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => close()}
                variant={'primary'}
              >
                닫기
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));
      return;
    }

    setRecordType(submittedRecordType);

    mutate(validatedFields.data.file);
  };

  return (
    <div className="w-full h-full flex justify-center items-center">
      <section className="w-full max-w-[520px] flex flex-col gap-2 mx-auto">
        <Text
          align="center"
          tag="h1"
          typography="typo-title-l-bold"
        >
          수탁사 기록서 만들기
        </Text>
        <Text
          align="center"
          className="text-muted-foreground"
          typography="typo-text-xl"
        >
          마킹된 자사용 기록서(.docx)를 업로드해주세요.
        </Text>
        <form
          className="w-full flex flex-col gap-2"
          onSubmit={handleSubmit}
        >
          <div className="flex justify-between pt-6">
            <Input
              accept=".docx"
              className="hidden"
              id="file-upload"
              name="file-upload"
              onChange={handleFileUpload}
              placeholder="파일을 선택해주세요."
              ref={fileInputRef}
              type="file"
            />
            <Label>자사용 기록서 업로드</Label>
            <Label
              asChild
              htmlFor="file-upload"
            >
              <Button
                onClick={handleFileUploadLabelButtonClick}
                size="default"
                type="button"
                variant="outline"
              >
                파일 업로드
              </Button>
            </Label>
          </div>

          <SwitchCase
            cases={{
              empty: (
                <div className="rounded h-[120px] px-3 bg-neutral-200 flex items-center justify-center">
                  <Text
                    align="center"
                    className="text-muted-foreground/60 my-auto"
                    typography="typo-text-xl"
                  >
                    업로드된 파일이 없습니다.
                  </Text>
                </div>
              ),
              'not-empty': (
                <ScrollArea className="rounded h-[120px] px-3 bg-white border">
                  <ul className="flex flex-col gap-2 my-3">
                    {uploadedFile && (
                      <li key={`${uploadedFile.name}-${uploadedFile.name}`}>
                        <div className="flex gap-1 flex-1 min-w-0 items-center">
                          <Text
                            className="truncate max-w-[450px]"
                            typography="typo-text-xl"
                          >
                            {uploadedFile.name}({formatFileSize(uploadedFile.size)})
                          </Text>
                          <Button
                            className="rounded-full"
                            onClick={handleFileRemove}
                            size="xs"
                            type="button"
                            variant="secondary"
                          >
                            <XIcon className="size-3" />
                          </Button>
                        </div>
                      </li>
                    )}
                  </ul>
                </ScrollArea>
              ),
            }}
            defaultCase={
              <Text
                align="center"
                className="text-muted-foreground/60 my-auto"
                typography="typo-text-m"
              >
                업로드된 파일이 없습니다.
              </Text>
            }
            value={uploadedFile ? 'not-empty' : 'empty'}
          />

          <div className="flex justify-center gap-3 pt-6">
            <Button
              className="hover:bg-primary/90 focus:ring-primary/20 focus:outline-none focus:ring-[3px]"
              name="record-type"
              pending={(!uploadedFile || isPending) && recordType === RECORD_TYPE.MANUFACTURE}
              size={'lg'}
              type="submit"
              value={RECORD_TYPE.MANUFACTURE}
            >
              제조지시 기록서
            </Button>
            <Button
              className="bg-[#8BCC6F] hover:bg-[#8BCC6F]/90 focus:ring-[#8BCC6F]/20 focus:outline-none focus:ring-[3px]"
              name="record-type"
              pending={(!uploadedFile || isPending) && recordType === RECORD_TYPE.PACKAGE}
              size={'lg'}
              type="submit"
              value={RECORD_TYPE.PACKAGE}
            >
              포장지시 기록서
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
